import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parseISO } from "date-fns";
import AnimatedPage from "../../../components/AnimatedPage";
import { dictionariesService, tradersService } from "../../../services";
import { Trader } from "../../../types/trader";
import SearchableSelect from "../../../components/SearchableSelect";
import { Currency } from "../../../types/common/currency";
import { useNotification } from "../../../contexts/NotificationContext";

interface TraderBalanceHistoryItem {
  id: string;
  amount: string;
  old_balance: string;
  new_balance: string;
  operation_type: string;
  target_field: string;
  description: string | null;
  created_at: string;
  admin_name: string | null;
  trader?: {
    name: string;
  };
  currency?: {
    iso_code: string;
  };
}

interface TraderBalanceHistoryResponse {
  items: TraderBalanceHistoryItem[];
  total: number;
}

interface TraderRegistryRef {
  handleExportXlsx: () => void;
  handleExportCsv: () => void;
}

const OPERATION_TYPES = [
  { value: "manual_update", label: "Ручное изменение" },
  { value: "add_funds", label: "Пополнение" },
  { value: "withdraw_funds", label: "Списание" },
  { value: "in_transaction_success", label: "Успешная IN-транзакция" },
  { value: "out_transaction_success", label: "Успешная OUT-транзакция" },
  { value: "in_transaction_success_trader", label: "IN: закрыто трейдером" },
  { value: "in_transaction_success_sla", label: "IN: закрыто по SLA" },
  { value: "in_transaction_success_admin", label: "IN: закрыто админом" },
  { value: "out_transaction_success_trader", label: "OUT: закрыто трейдером" },
  { value: "out_transaction_success_sla", label: "OUT: закрыто по SLA" },
  { value: "out_transaction_success_admin", label: "OUT: закрыто админом" },
  { value: "penalty", label: "Штраф" },
  { value: "refund_credit", label: "Зачисление по Refund" },
  { value: "chargeback_credit", label: "Зачисление по Chargeback" },
  { value: "cyber_compliance_credit", label: "Зачисление по Cyber Compliance" },
];

const getOperationLabel = (value: string) =>
  OPERATION_TYPES.find((item) => item.value === value)?.label || value;

const TraderRegistry = forwardRef<TraderRegistryRef>((props, ref) => {
  const [items, setItems] = useState<TraderBalanceHistoryItem[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filterTraderId, setFilterTraderId] = useState<string>("all");
  const [filterCurrencyId, setFilterCurrencyId] = useState<string>("all");
  const [filterOperationType, setFilterOperationType] = useState<string>("all");
  const [transactionIdInput, setTransactionIdInput] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const totalPages = Math.ceil(total / rowsPerPage);
  const { showNotification } = useNotification();

  const parsePickerDate = (value: string): Date | null => {
    if (!value) return null;
    return parseISO(value);
  };

  const formatPickerDate = (value: Date | null): string => {
    if (!value) return "";
    return format(value, "yyyy-MM-dd");
  };

  const today = new Date();
  const startDateValue = parsePickerDate(startDate);
  const endDateValue = parsePickerDate(endDate);
  const startDateMax = endDateValue && endDateValue < today ? endDateValue : today;

  const loadFiltersData = useCallback(async () => {
    try {
      const [tradersData, currenciesData] = await Promise.all([
        tradersService.getTraders({ is_archive: false }),
        dictionariesService.getCurrencies(),
      ]);
      setTraders(tradersData || []);
      setCurrencies(currenciesData || []);
    } catch (err) {
      console.error("Error loading registry filters data:", err);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: TraderBalanceHistoryResponse =
        await tradersService.getTraderBalancesHistory({
          trader_id: filterTraderId === "all" ? undefined : filterTraderId,
          currency_id: filterCurrencyId === "all" ? undefined : filterCurrencyId,
          operation_type:
            filterOperationType === "all" ? undefined : filterOperationType,
          transaction_id: transactionId.trim() || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error("Error loading trader balances history:", err);
      setError(err?.response?.data?.detail || "Ошибка при загрузке реестра");
    } finally {
      setLoading(false);
    }
  }, [endDate, filterCurrencyId, filterOperationType, filterTraderId, page, rowsPerPage, startDate, transactionId]);

  useEffect(() => {
    loadFiltersData();
  }, [loadFiltersData]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setTransactionId(transactionIdInput.trim());
      setPage(0);
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [transactionIdInput]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatDateTime = (value: string) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("ru-RU");
  };

  const handleExportBalancesHistory = async (formatValue: "csv" | "xlsx") => {
    if (startDate && endDate && startDate > endDate) {
      showNotification('Дата "С даты" не может быть больше даты "По дату"', "error");
      return;
    }

    try {
      const params = {
        format: formatValue,
        trader_id: filterTraderId === "all" ? undefined : filterTraderId,
        currency_id: filterCurrencyId === "all" ? undefined : filterCurrencyId,
        operation_type:
          filterOperationType === "all" ? undefined : filterOperationType,
        transaction_id: transactionIdInput.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };

      const response = await tradersService.exportTraderBalancesHistory(params);
      const contentType =
        response.headers["content-type"] ||
        (formatValue === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8");
      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers["content-disposition"];
      const filenameMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^\";\n]+)/i);
      const decodedFilename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, ""))
        : `trader-balances-history.${formatValue}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = decodedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification("Файл успешно выгружен", "success");
    } catch (err: any) {
      console.error("Error exporting trader balances history:", err);
      showNotification("Ошибка при выгрузке файла", "error");
    }
  };

  useImperativeHandle(ref, () => ({
    handleExportXlsx: () => {
      void handleExportBalancesHistory("xlsx");
    },
    handleExportCsv: () => {
      void handleExportBalancesHistory("csv");
    },
  }));

  return (
    <AnimatedPage>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          gap={2}
          flexWrap="wrap"
        >
          <Typography variant="h5">Реестр</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              label="ID транзакции"
              value={transactionIdInput}
              onChange={(event) => {
                setTransactionIdInput(event.target.value);
              }}
              sx={{ minWidth: 260 }}
            />
            <Box sx={{ minWidth: 220 }}>
              <SearchableSelect
                label="Трейдер"
                size="small"
                value={filterTraderId === 'all' ? '' : filterTraderId}
                options={traders.map((trader) => ({ id: trader.id, label: trader.name }))}
                onChange={(value) => {
                  setFilterTraderId(value || 'all');
                  setPage(0);
                }}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Валюта</InputLabel>
              <Select
                value={filterCurrencyId}
                label="Валюта"
                onChange={(event: SelectChangeEvent<string>) => {
                  setFilterCurrencyId(event.target.value);
                  setPage(0);
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 320 },
                  },
                }}
              >
                <MenuItem value="all">Все</MenuItem>
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.iso_code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Действие</InputLabel>
              <Select
                value={filterOperationType}
                label="Действие"
                onChange={(event: SelectChangeEvent<string>) => {
                  setFilterOperationType(event.target.value);
                  setPage(0);
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 320 },
                  },
                }}
              >
                <MenuItem value="all">Все</MenuItem>
                {OPERATION_TYPES.map((operationType) => (
                  <MenuItem key={operationType.value} value={operationType.value}>
                    {operationType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="С даты"
              value={startDateValue}
              onChange={(value) => {
                setStartDate(formatPickerDate(value));
                setPage(0);
              }}
              format="dd.MM.yyyy"
              maxDate={startDateMax}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                } as Partial<React.ComponentProps<typeof TextField>>,
              }}
            />
            <DatePicker
              label="По дату"
              value={endDateValue}
              onChange={(value) => {
                setEndDate(formatPickerDate(value));
                setPage(0);
              }}
              format="dd.MM.yyyy"
              minDate={startDateValue || undefined}
              maxDate={today}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 },
                } as Partial<React.ComponentProps<typeof TextField>>,
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setFilterTraderId("all");
                setFilterCurrencyId("all");
                setFilterOperationType("all");
                setTransactionIdInput("");
                setTransactionId("");
                setStartDate("");
                setEndDate("");
                setPage(0);
              }}
            >
              Сбросить
            </Button>
          </Box>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Трейдер</TableCell>
                  <TableCell>Валюта</TableCell>
                  <TableCell>Операция</TableCell>
                  <TableCell>Поле</TableCell>
                  <TableCell>Сумма</TableCell>
                  <TableCell>Старый баланс</TableCell>
                  <TableCell>Новый баланс</TableCell>
                  <TableCell>Админ</TableCell>
                  <TableCell>Описание</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length ? (
                  items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                      <TableCell>{row.trader?.name || "-"}</TableCell>
                      <TableCell>{row.currency?.iso_code || "-"}</TableCell>
                      <TableCell>
                        {row.operation_type ? getOperationLabel(row.operation_type) : "-"}
                      </TableCell>
                      <TableCell>{row.target_field || "-"}</TableCell>
                      <TableCell>{row.amount || "-"}</TableCell>
                      <TableCell>{row.old_balance || "-"}</TableCell>
                      <TableCell>{row.new_balance || "-"}</TableCell>
                      <TableCell>{row.admin_name || "-"}</TableCell>
                      <TableCell>{row.description || "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      Данные отсутствуют
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {totalPages > 1 && !loading && !error && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, newPage) => setPage(newPage - 1)}
              color="primary"
              showFirstButton
              showLastButton
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  disabled={item.page === page + 1}
                />
              )}
            />
          </Stack>
        )}
      </Box>
    </AnimatedPage>
  );
});

export default TraderRegistry;
